/**
 * TransmissionDetailPanel — Side panel showing execution details for a selected transmission.
 * Dispatches fetchExecution() on selection, reads state from execution slice.
 */
import { useEffect } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { fetchExecution, clearExecution } from 'src/store/slices/execution/executionSlice';
import { ExecutionMetadata } from './ExecutionMetadata';
import { StatusTimeline } from 'src/components/common/StatusTimeline';

interface TransmissionDetailPanelProps {
  item: { executionArn?: string | null };
  onClose: () => void;
}

export const TransmissionDetailPanel = ({ item, onClose }: TransmissionDetailPanelProps) => {
  const dispatch = useAppDispatch();
  const { data, loading, error } = useAppSelector(state => state.execution);

  useEffect(() => {
    if (item?.executionArn) dispatch(fetchExecution(item.executionArn));
    return () => { dispatch(clearExecution()); };
  }, [item?.executionArn, dispatch]);

  return (
    <SpaceBetween size="m">
      <Header variant="h3" actions={<Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close panel" />}>
        Execution Details
      </Header>

      {loading && (
        <Box textAlign="center" padding={{ vertical: 'l' }}>
          <Spinner size="large" />
        </Box>
      )}

      {error && !loading && (
        <StatusIndicator type="error">{error}</StatusIndicator>
      )}

      {data && !loading && (
        <SpaceBetween size="l">
          <ExecutionMetadata data={data} />
          {data.statusTimeline.length > 0 && (
            <StatusTimeline title="Execution Lifecycle" timeline={data.statusTimeline} />
          )}
        </SpaceBetween>
      )}

      {!item?.executionArn && !loading && (
        <Box color="text-body-secondary">No execution ID available for this transmission.</Box>
      )}
    </SpaceBetween>
  );
};
